import React, { useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { GeneralStyles as theme } from '../styles/generalStyles';
import Tims4GoodContext from '../../context/timsForGoodContext';
import { t } from '../../translations';

// Singleton YouTube API loader - ensures script loads once and all players initialize
let youtubeApiPromise = null;
const loadYoutubeApi = () => {
  if (youtubeApiPromise) return youtubeApiPromise;

  youtubeApiPromise = new Promise((resolve) => {
    // Already loaded
    if (window.YT && window.YT.Player) {
      resolve(window.YT);
      return;
    }

    // Load script
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.body.appendChild(script);

    window.onYouTubeIframeAPIReady = () => {
      resolve(window.YT);
    };
  });

  return youtubeApiPromise;
};

const VideoResponsive = styled.div`
  position: relative;
  aspect-ratio: 16 / 9;
  width: 100%;
  overflow: hidden;
  max-width: 1050px;
  background-color: #000;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

const VideoContainer = styled.div``;

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 10;
`;

const OverlayText = styled.p`
  color: ${theme.colors.white};
  font-size: ${theme.fontSize.preAtom};
  margin-bottom: ${theme.spacings.giga};
  max-width: 380px;
  line-height: 1.5;
  text-align: center;
  z-index: 11;

  a {
    color: ${theme.colors.white};
    text-decoration: underline;

    &:hover,
    &:focus {
      opacity: 0.8;
    }
  }
`;

const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: 1;
`;

const ConsentButton = styled.button`
  z-index: 11;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${theme.spacings.bit};
  padding: ${theme.spacings.exa} ${theme.spacings.giga};
  border: none;
  border-radius: 12px;
  background-color: ${theme.colors.white};
  color: ${theme.colors.red1};
  cursor: pointer;
  font-family: Brewed;
  font-size: ${theme.fontSize.preAtom};
  font-style: normal;
  font-weight: ${theme.fontWeight.bold};
  line-height: 24px;
  letter-spacing: 0.08px;

  &:focus {
    ${theme.focus.brown};
  }
`;

const YoutubeEmbed = ({ videoId, iframeTitle }) => {
  const { youtubeConsent, setYoutubeConsent } = useContext(Tims4GoodContext);
  const playerId = `yt-player-${videoId}`;
  const playerRef = useRef(null);

  const YoutubeConsentThumbnail = t('common')('youtubeConsent');

  useEffect(() => {
    if (!youtubeConsent) {
      return () => {};
    }

    let isMounted = true;

    loadYoutubeApi().then((YT) => {
      if (!isMounted) return;

      playerRef.current = new YT.Player(playerId, {
        videoId,
        host: 'https://www.youtube-nocookie.com',
        playerVars: {
          rel: 0,
          modestbranding: 1,
          playsinline: 1,
          autoplay: 0,
        },
      });
    });

    // Cleanup: destroy player on unmount
    return () => {
      isMounted = false;
      if (playerRef.current) {
        playerRef.current.destroy();
      }
    };
  }, [youtubeConsent, videoId, playerId]);

  // Keyboard handler for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!youtubeConsent) {
        setYoutubeConsent(true);
      } else if (playerRef.current && playerRef.current.getPlayerState) {
        const state = playerRef.current.getPlayerState();
        if (state === window.YT.PlayerState.PLAYING) {
          playerRef.current.pauseVideo();
        } else {
          playerRef.current.playVideo();
        }
      }
    }
  };

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  if (youtubeConsent) {
    return (
      <VideoResponsive tabIndex="0" onKeyDown={handleKeyDown}>
        <VideoContainer id={playerId} title={iframeTitle} />
      </VideoResponsive>
    );
  }

  return (
    <VideoResponsive tabIndex="0" onKeyDown={handleKeyDown}>
      <Thumbnail src={thumbnailUrl} alt={iframeTitle} />
      <Overlay>
        <OverlayText>
          {YoutubeConsentThumbnail.textStart}
          {' '}
          <a
            href={YoutubeConsentThumbnail.url}
            target="_blank"
            rel="noopener noreferrer"
          >
            {YoutubeConsentThumbnail.linkText}
          </a>
          {' '}
          {YoutubeConsentThumbnail.textMiddle}
          {' '}
          <a
            href={YoutubeConsentThumbnail.url2}
            target="_blank"
            rel="noopener noreferrer"
          >
            {YoutubeConsentThumbnail.linkText2}
          </a>
          {YoutubeConsentThumbnail.textEnd}
        </OverlayText>
        <ConsentButton type="button" onClick={() => setYoutubeConsent(true)}>
          {YoutubeConsentThumbnail.button}
        </ConsentButton>
      </Overlay>
    </VideoResponsive>
  );
};

YoutubeEmbed.propTypes = {
  videoId: PropTypes.string.isRequired,
  iframeTitle: PropTypes.string.isRequired,
};

export default YoutubeEmbed;
